import { alignUnits, units, wordDiff } from '@addictivegroup/addictive-richtext/schema'

/**
 * Two versions of the catalogue compared (D-316), as an offer's revision marks its changes: a
 * text's paragraphs, headings and list items — the same, changed (word to word), new, or gone
 * where they stood; and lists of lines (a template's structure, a specification) the same way.
 */
const textOf = (n) => (n?.type === 'text' ? n.text || '' : (n?.content || []).map(textOf).join(n?.type === 'listItem' ? ' ' : ''))
const kindOf = (n) => (n?.type === 'heading' ? 'heading' : n?.type === 'listItem' ? 'item' : n?.type === 'image' ? 'image' : 'paragraph')
const lineOf = (n) => (n?.type === 'image' ? `🖼 ${n.attrs?.title || ''}`.trim() : textOf(n).trim())

/**
 * Lines against lines: each as it stands now — same, changed (with the words that differ), new —
 * and the ones that went, each before the line it stood before.
 *
 * @param {string[]} olds @param {string[]} nows
 * @returns {Array<{state: 'same'|'changed'|'added'|'removed', text: string, parts?: Array, index: number}>}
 */
export function compareLines(olds, nows, kinds = { old: [], now: [] }) {
  const compatible = (j, i) => (kinds.old[j] ?? 'line') === (kinds.now[i] ?? 'line')
  const { was, gone, before } = alignUnits(olds, nows, compatible)
  const out = []
  const removedAt = new Map()
  olds.forEach((t, j) => {
    if (!gone[j]) return
    const at = before[j] >= 0 ? before[j] : nows.length
    if (!removedAt.has(at)) removedAt.set(at, [])
    removedAt.get(at).push({ state: 'removed', text: t, kind: kinds.old[j] ?? 'line', index: j })
  })
  nows.forEach((t, i) => {
    for (const r of removedAt.get(i) || []) out.push(r)
    const kind = kinds.now[i] ?? 'line'
    if (was[i] === null) out.push({ state: 'added', text: t, kind, index: i })
    else if (was[i] === t) out.push({ state: 'same', text: t, kind, index: i })
    else out.push({ state: 'changed', text: t, kind, parts: wordDiff(was[i], t), index: i })
  })
  for (const r of removedAt.get(nows.length) || []) out.push(r)
  return out
}

/** A text (a TipTap doc) against another, unit by unit. */
export function compareDocs(oldDoc, newDoc) {
  const o = units(oldDoc || { content: [] })
  const n = units(newDoc || { content: [] })
  return compareLines(o.map(lineOf), n.map(lineOf), { old: o.map(kindOf), now: n.map(kindOf) })
}

/** Rows of a component by their key: new, gone, changed in their name, price or quantity. */
export function compareRows(oldRows = [], newRows = []) {
  const byKey = new Map(oldRows.map((r) => [r.row_key, r]))
  const seen = new Set()
  const out = newRows.map((r) => {
    const was = byKey.get(r.row_key)
    seen.add(r.row_key)
    if (!was) return { state: 'added', now: r }
    const fields = ['name', 'unit', 'unit_price', 'discount', 'period'].filter((k) => String(was.values?.[k] ?? '') !== String(r.values?.[k] ?? ''))
    const other = Number(was.default_quantity ?? 1) !== Number(r.default_quantity ?? 1) || !!was.is_optional !== !!r.is_optional || JSON.stringify(was.prices || {}) !== JSON.stringify(r.prices || {})
    return { state: fields.length || other ? 'changed' : 'same', now: r, was, fields }
  })
  for (const r of oldRows) if (!seen.has(r.row_key)) out.push({ state: 'removed', was: r })
  return out
}
